import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Organization } from "./contact.types";
import { createOrganization, fetchOrganizations } from "./contact.service";

interface Props {
  value: Organization | null;
  onChange: (org: Organization | null) => void;
  error?: boolean;
}

export default function OrganizationSelect({ value, onChange, error }: Props) {
  const [options, setOptions] = React.useState<Organization[]>([]);
  const [inputValue, setInputValue] = React.useState(value?.name || "");
  const [loading, setLoading] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    (async () => setOptions(await fetchOrganizations("")))();
  }, []);

  const debouncedSearch = (q: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setOptions(await fetchOrganizations(q));
    }, 500);
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      e.stopPropagation();
      const exists = options.find(
        (o) => o.name.toLowerCase() === inputValue.trim().toLowerCase()
      );
      if (!exists) {
        try {
          setLoading(true);
          const newOrg = await createOrganization(inputValue.trim());
          if (newOrg?.name) {
            setOptions((prev) => [...prev, newOrg]);
            onChange(newOrg);
            setInputValue(newOrg.name);
          }
        } catch {
          /* ignore */
        } finally {
          setLoading(false);
        }
      }
    }
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      loading={loading}
      options={options}
      getOptionLabel={(o: any) => (typeof o === "string" ? o : o.name)}
      value={value}
      inputValue={inputValue}
      onInputChange={(_e, v) => {
        setInputValue(v);
        debouncedSearch(v);
      }}
      onChange={(_e, selected: any) => {
        if (selected?.name) {
          onChange(selected);
          setInputValue(selected.name);
        } else if (!selected) {
          onChange(null);
        }
      }}
      slotProps={{ popper: { sx: { zIndex: 20000 } } }}
      noOptionsText={
        inputValue ? `Press Enter to create "${inputValue}"` : "Start typing to search organizations"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          placeholder="Search or create organization"
          error={error}
        />
      )}
    />
  );
}